import { Pagination } from '../../lib/interfaces/Pagination.interface'
import { CountByType } from '../../lib/interfaces/CountByType.interface'
import { JobApplicant } from '../../lib/interfaces/JobApplicant.interface'
import { LocationJobPosition } from '../../lib/interfaces/JobPosition.interface'
import { ApplicationStatus } from '../../lib/interfaces/ApplicationStatus.interface'
import { ApplicationFilterParameters } from '../../lib/interfaces/ApplicationFilterParameters.interface'

import {
  actionTypes,
  ActionTypes,
  ApplicationsUpdateSuccessAction,
  ApplicationsApproveSuccessAction,
  ApplicationsSetFilterParamsAction,
  ApplicationsFetchListSuccessAction,
  ApplicationsFetchFilesSuccessAction,
  ApplicationsPostCommentSuccessAction,
  ApplicationsFetchSummarySuccessAction,
  ApplicationsUpdateStatusSuccessAction,
  ApplicationsFetchApplicationSuccessAction,
  ApplicationsConvertToEmployeeSuccessAction,
  ApplicationsFetchNextEmployeeIdSuccessAction,
  ApplicationsUpdateLocationJobPositionOpenAction,
  ApplicationsFetchLocationJobPositionsListSuccessAction,
} from './actions'

const get = require('lodash/get')
const find = require('lodash/find')
const findIndex = require('lodash/findIndex')

const initialFilterParams: ApplicationFilterParameters = {
  page: 0,
  sortOrder: 'desc',
  location: undefined,
  sortBy: 'dateCreated',
  status: [
    ApplicationStatus.COMPLETED,
    ApplicationStatus.INTERVIEW,
    ApplicationStatus.IN_REVIEW
  ]
}

export interface ApplicationsState {
  readonly isLoading: boolean
  readonly isFetching: boolean
  readonly nextEmployeeId?: string
  readonly isFetchingFiles: boolean
  readonly isUploadingFile: boolean
  readonly isPostingComment: boolean
  readonly isFetchingSummary: boolean
  readonly summaryData?: CountByType[]
  readonly isFetchingApplications: boolean
  readonly applicationDetails?: JobApplicant
  readonly isFetchingNextEmployeeId: boolean
  readonly filterParams: ApplicationFilterParameters
  readonly locationJobPositions?: LocationJobPosition[]
  readonly paginatedApplications?: Pagination<JobApplicant>
}

export const initialState: ApplicationsState = {
  isLoading: false,
  isFetching: false,
  summaryData: undefined,
  isFetchingFiles: false,
  isUploadingFile: false,
  isPostingComment: false,
  isFetchingSummary: false,
  nextEmployeeId: undefined,
  applicationDetails: undefined,
  isFetchingApplications: false,
  locationJobPositions: undefined,
  isFetchingNextEmployeeId: false,
  filterParams: initialFilterParams
}

export function reducer(state: ApplicationsState = initialState, action: ActionTypes): ApplicationsState {
  switch (action.type) {
    case actionTypes.APPLICATIONS_FETCH:
      return {
        ...state,
        applicationDetails: undefined
      }

    case actionTypes.APPLICATIONS_FETCH_FAILED:
      return {
        ...state,
        applicationDetails: undefined
      }

    case actionTypes.APPLICATIONS_FETCH_SUCCESS: {
      const { application } = (action as ApplicationsFetchApplicationSuccessAction).payload

      return {
        ...state,
        applicationDetails: application
      }
    }


    case actionTypes.APPLICATIONS_FETCH_LOCATION_JOB_POSITIONS_LIST:
      return {
        ...state,
        isFetching: true
      }

    case actionTypes.APPLICATIONS_FETCH_LOCATION_JOB_POSITIONS_LIST_SUCCESS:
      return {
        ...state,
        isFetching: false,
        locationJobPositions: (action as ApplicationsFetchLocationJobPositionsListSuccessAction).payload.locationJobPositions
      }

    case actionTypes.APPLICATIONS_FETCH_LOCATION_JOB_POSITIONS_LIST_FAILED:
      return {
        ...state,
        isFetching: false
      }

    case actionTypes.APPLICATIONS_UPDATE_LOCATION_JOB_POSITION_OPEN:
      return {
        ...state,
        isLoading: true
      }

    case actionTypes.APPLICATIONS_UPDATE_LOCATION_JOB_POSITION_OPEN_SUCCESS: {
      const { open, locationJobPositionId } = (action as ApplicationsUpdateLocationJobPositionOpenAction).payload

      const positions = [ ...(state.locationJobPositions || []) ]
      const updatedPosition = find(positions, { id: locationJobPositionId })
      if (updatedPosition) {
        updatedPosition.enabled = open
      }

      return {
        ...state,
        isLoading: false,
        locationJobPositions: positions
      }
    }

    case actionTypes.APPLICATIONS_UPDATE_LOCATION_JOB_POSITION_OPEN_FAILED:
      return {
        ...state,
        isLoading: false
      }

    case actionTypes.APPLICATIONS_FETCH_LIST:
      return {
        ...state,
        isFetchingApplications: true,
        paginatedApplications: undefined
      }

      case actionTypes.APPLICATIONS_FETCH_LIST_FAILED:
        return {
          ...state,
          isFetchingApplications: false
        }

      case actionTypes.APPLICATIONS_FETCH_LIST_SUCCESS: {
        const { paginatedApplications } = (action as ApplicationsFetchListSuccessAction).payload

        return {
          ...state,
          paginatedApplications,
          isFetchingApplications: false
        }
      }

      case actionTypes.APPLICATIONS_FETCH_SUMMARY:
        return {
          ...state,
          isFetchingSummary: true
        }

      case actionTypes.APPLICATIONS_FETCH_SUMMARY_FAILED:
        return {
          ...state,
          isFetchingSummary: false
        }

      case actionTypes.APPLICATIONS_FETCH_SUMMARY_SUCCESS: {
        const { summary } = (action as ApplicationsFetchSummarySuccessAction).payload
        return {
          ...state,
          summaryData: summary,
          isFetchingSummary: false,
        }
      }

    case actionTypes.APPLICATIONS_FETCH_FILES:
      return {
        ...state,
        isFetchingFiles: true
      }

    case actionTypes.APPLICATIONS_FETCH_FILES_FAILED: {
      return {
        ...state,
        isFetchingFiles: false
      }
    }

    case actionTypes.APPLICATIONS_FETCH_FILES_SUCCESS: {
      const successAction = action as ApplicationsFetchFilesSuccessAction

      const files = successAction.payload.files
      const applicationId = successAction.payload.applicationId

      const applications: JobApplicant[] = [ ...get(state, 'applications', []) ]
      const applicationIndex = findIndex(applications, { id: applicationId })

      if (applicationIndex >= 0) {
        const application = applications[applicationIndex]
        application.files = files
        applications.splice(applicationIndex, 1, application)
      }

      const newState = { ...state, isFetchingFiles: false }

      // if (state.currentApplication && state.currentApplication.id === applicationId) {
      //   const currentApplication = { ...state.currentApplication } as JobApplicant

      //   currentApplication.files = files
      //   newState.currentApplication = currentApplication
      // }

      if (state.applicationDetails && state.applicationDetails.id === applicationId) {
        const applicationDetails = { ...state.applicationDetails } as JobApplicant

        applicationDetails.files = files
        newState.applicationDetails = applicationDetails
      }

      newState.isFetchingFiles = false

      return newState
    }

    case actionTypes.APPLICATIONS_UPLOAD_FILE:
      return { ...state, isUploadingFile: true }

    case actionTypes.APPLICATIONS_UPLOAD_FILE_SUCCESS:
      return { ...state, isUploadingFile: false }

    case actionTypes.APPLICATIONS_UPLOAD_FILE_FAILED:
      return { ...state, isUploadingFile: false }

    case actionTypes.APPLICATIONS_POST_COMMENT:
      return {
        ...state,
        isPostingComment: true
      }

    case actionTypes.APPLICATIONS_POST_COMMENT_SUCCESS: {
      const applicationDetails = { ...state.applicationDetails } as JobApplicant

      const postCommentSuccessAction = action as ApplicationsPostCommentSuccessAction
      if (applicationDetails && applicationDetails.id === postCommentSuccessAction.payload.applicationId) {
        applicationDetails.comments = [ ...(applicationDetails.comments || []), postCommentSuccessAction.payload.comment ]
      }

      return {
        ...state,
        applicationDetails,
        isPostingComment: false
      }
    }

    case actionTypes.APPLICATIONS_POST_COMMENT_FAILED:
      return {
        ...state,
        isPostingComment: false
      }

    case actionTypes.APPLICATIONS_UPDATE_STATUS_SUCCESS: {
      const successAction = action as ApplicationsUpdateStatusSuccessAction

      const applicationDetails = state.applicationDetails
      if (applicationDetails && applicationDetails.id === successAction.payload.application.id) {
        return {
          ...state,
          applicationDetails: {
            ...successAction.payload.application,
            files: applicationDetails.files
          }
        }
      }
      
      return state
    }

    case actionTypes.APPLICATIONS_APPROVE_SUCCESS: {
      const successAction = action as ApplicationsApproveSuccessAction

      const applicationDetails = state.applicationDetails
      if (applicationDetails && applicationDetails.id === successAction.payload.application.id) {
        return {
          ...state,
          applicationDetails: {
            ...successAction.payload.application,
            files: applicationDetails.files
          }
        }
      }
      
      return state
    }

    case actionTypes.APPLICATIONS_FETCH_NEXT_EMPLOYEE_ID:
      return {
        ...state,
        nextEmployeeId: undefined,
        isFetchingNextEmployeeId: true
      }

    case actionTypes.APPLICATIONS_FETCH_NEXT_EMPLOYEE_ID_FAILED:
      return {
        ...state,
        isFetchingNextEmployeeId: false
      }

    case actionTypes.APPLICATIONS_FETCH_NEXT_EMPLOYEE_ID_SUCCESS: {
      const successAction = action as ApplicationsFetchNextEmployeeIdSuccessAction

      return {
        ...state,
        isFetchingNextEmployeeId: false,
        nextEmployeeId: successAction.payload.nextEmployeeId
      }
    }

    case actionTypes.APPLICATIONS_CONVERT_TO_EMPLOYEE_SUCCESS: {
      const successAction = action as ApplicationsConvertToEmployeeSuccessAction

      const applicationDetails = state.applicationDetails
      if (applicationDetails && applicationDetails.id === successAction.payload.application.id) {
        return {
          ...state,
          applicationDetails: {
            ...successAction.payload.application,
            files: applicationDetails.files
          }
        }
      }
    }

    case actionTypes.APPLICATIONS_SET_FILTER_PARAMS: {
      const filterParams = {
        ...state.filterParams,
        ...(action as ApplicationsSetFilterParamsAction).payload.filterParams
      }

      return {
        ...state,
        filterParams
      }
    }

    case actionTypes.APPLICATIONS_UPDATE_SUCCESS: {
      const successAction = action as ApplicationsUpdateSuccessAction

      const applicationDetails = state.applicationDetails
      if (applicationDetails && applicationDetails.id === successAction.payload.application.id) {
        return {
          ...state,
          applicationDetails: {
            ...successAction.payload.application,
            files: applicationDetails.files
          }
        }
      }
    }

    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/store/applications/reducer.ts